import {
  WEEKLY_WALKTHROUGH_EVENTS,
  type WeeklyWalkthrough,
} from "./weekly-walkthrough-types";

export const CORE_WEEKLY_WALKTHROUGHS = {
  "file-room-updates": {
    id: "file-room-updates",
    title: "Weekly Updates in File Room",
    ctaLabel: "Open Updates",
    targetRoute: "/?weeklyWalkthrough=file-room-updates",
    targetAction: "Open Apparatus -> File Room -> Updates.",
    outcome: "Find the weekly app summary and use each CTA block.",
    prerequisites: "Signed-in user with File Room access.",
    targetTab: "analysis",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.fileRoomUpdates,
    dispatchDelayMs: 260,
    steps: [
      "Open File Room from Apparatus.",
      "Expand Updates at the bottom.",
      "Select the current weekly note and run each CTA.",
    ],
  },
  "scout-create": {
    id: "scout-create",
    title: "Create a Scout from CAO Chat",
    ctaLabel: "Try workflow",
    targetRoute: "/?weeklyWalkthrough=scout-create",
    targetAction: "Open Scouts and start Create via chat.",
    outcome:
      "Turn an objective, cadence, and connector scope into a saved Scout.",
    prerequisites: "Signed-in user; provider readiness should be green.",
    targetTab: "scouts",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.scoutsFocusCreate,
    dispatchDelayMs: 180,
    steps: [
      "Open Scouts.",
      "Choose Create via chat.",
      "Answer objective, cadence, provider, and connector-scope questions.",
    ],
  },
  "equities-sentiment": {
    id: "equities-sentiment",
    title: "Review Equities Sentiment",
    ctaLabel: "Open demo",
    targetRoute: "/?weeklyWalkthrough=equities-sentiment",
    targetAction: "Open Dashboard and hover the TiltScout fuse.",
    outcome: "Understand the read, event window, and what would change it.",
    prerequisites:
      "RiskFlow, Desk Plan, and Arbitrum sources improve the read.",
    targetTab: "dashboard",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.equitiesSentimentFocus,
    dispatchDelayMs: 220,
    steps: [
      "Open Dashboard.",
      "Hover the TiltScout fuse in the top bar.",
      "Read Chart for Basis GEX, then compare Window, Invalidation, and Changes.",
    ],
  },
  "desktop-auth-restore": {
    id: "desktop-auth-restore",
    title: "Restore Desktop Sign-In",
    ctaLabel: "Review sign-in",
    targetRoute: "/?weeklyWalkthrough=desktop-auth-restore",
    targetAction: "Open Settings -> Privacy & Security.",
    outcome: "Use Chrome sign-in to restore the Desktop session.",
    prerequisites: "Desktop app; Chrome should hold the Fintheon Web session.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "privacy-security" },
    dispatchDelayMs: 160,
    steps: [
      "Open Privacy & Security.",
      "Confirm the current account and session state.",
      "Use Switch only when Desktop needs to restore through Chrome.",
    ],
  },
  "cao-action-cards": {
    id: "cao-action-cards",
    title: "Use CAO Action Cards",
    ctaLabel: "Ask CAO",
    targetRoute: "/?weeklyWalkthrough=cao-action-cards",
    targetAction: "Open CAO chat and ask for a plan or report card.",
    outcome: "Review visible cards before approving any app action.",
    prerequisites: "Signed-in user with provider health ready.",
    targetTab: "analysis",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.caoChat,
    dispatchDelayMs: 180,
    steps: [
      "Open CAO chat.",
      "Ask Harper for a plan, source packet, or checklist.",
      "Review the card and approve only the intended action.",
    ],
  },
  "narrativeflow-workspace": {
    id: "narrativeflow-workspace",
    title: "Open NarrativeFlow Workspace",
    ctaLabel: "Open demo",
    targetRoute: "/?weeklyWalkthrough=narrativeflow-workspace",
    targetAction: "Open Consilium -> NarrativeFlow.",
    outcome:
      "Continue a narrative with Workspace, Research, and Receipts visible.",
    prerequisites: "Signed-in user; an existing desk map improves context.",
    targetTab: "analysis",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.narrativeflowWorkspace,
    dispatchDelayMs: 120,
    steps: [
      "Open NarrativeFlow.",
      "Pick Workspace, Research, or Receipts.",
      "Use CAO/NarrativeFlow prompts for approval-gated edits.",
    ],
  },
  "provider-picker-anchor": {
    id: "provider-picker-anchor",
    title: "Switch CAO Provider",
    ctaLabel: "Try workflow",
    targetRoute: "/?weeklyWalkthrough=provider-picker-anchor",
    targetAction: "Open CAO chat and use the provider pill.",
    outcome: "Switch provider from the anchored composer popover.",
    prerequisites: "Signed-in user; configure provider access first if needed.",
    targetTab: "analysis",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.caoChat,
    dispatchDelayMs: 180,
    steps: [
      "Open CAO chat.",
      "Click the provider pill beside the composer.",
      "Pick Grok, OpenAI, DeepSeek, or Nous Portal without losing context.",
    ],
  },
  "provider-scope": {
    id: "provider-scope",
    title: "Review Provider Scope",
    ctaLabel: "Review setting",
    targetRoute: "/?weeklyWalkthrough=provider-scope",
    targetAction: "Open Settings -> Provider API.",
    outcome: "Confirm AI provider choices are scoped to the signed-in user.",
    prerequisites: "Signed-in user with provider access configured as needed.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "fintheon-api" },
    dispatchDelayMs: 160,
    steps: [
      "Open Provider API.",
      "Review the default CAO provider and provider access rows.",
      "Retry CAO, Scouts, or NarrativeFlow after changing the provider.",
    ],
  },
  "grok-provider-recovery": {
    id: "grok-provider-recovery",
    title: "Refresh Grok Provider",
    ctaLabel: "Review setting",
    targetRoute: "/?weeklyWalkthrough=grok-provider-recovery",
    targetAction: "Open Settings -> Provider API.",
    outcome: "Confirm Grok OAuth health before CAO, Scouts, or NarrativeFlow.",
    prerequisites:
      "Desktop uses Fintheon provider connections; web/mobile use saved provider keys.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "fintheon-api" },
    dispatchDelayMs: 160,
    steps: [
      "Open Settings.",
      "Choose Provider API.",
      "Use Reconnect or Refresh Grok if OAuth is stale.",
    ],
  },
  "settings-personalization": {
    id: "settings-personalization",
    title: "Review Personalization Settings",
    ctaLabel: "Review setting",
    targetRoute: "/?weeklyWalkthrough=settings-personalization",
    targetAction: "Open Settings -> Personalization.",
    outcome: "Tune themes, fonts, app icons, and color variables.",
    prerequisites: "Desktop or desktop-width web surface.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "personalization" },
    dispatchDelayMs: 120,
    steps: [
      "Open Settings.",
      "Choose Personalization.",
      "Review presets, color variables, theme name, font mode, and app icon.",
    ],
  },
  "settings-browsers": {
    id: "settings-browsers",
    title: "Review Browser Sources",
    ctaLabel: "Review setting",
    targetRoute: "/?weeklyWalkthrough=settings-browsers",
    targetAction: "Open Settings -> Browsers.",
    outcome: "Check browser sources, defaults, and custom URLs.",
    prerequisites: "Desktop or desktop-width web surface.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "browsers" },
    dispatchDelayMs: 120,
    steps: [
      "Open Settings.",
      "Choose Browsers.",
      "Review sources, default browser, layout, and URLs.",
    ],
  },
  "settings-lockout": {
    id: "settings-lockout",
    title: "Review Lockout Controls",
    ctaLabel: "Review setting",
    targetRoute: "/?weeklyWalkthrough=settings-lockout",
    targetAction: "Open Settings -> Lockout.",
    outcome: "Confirm timers, auto-lock rules, and allowed-site controls.",
    prerequisites: "Desktop app gives the richest lockout controls.",
    targetTab: "settings",
    eventName: WEEKLY_WALKTHROUGH_EVENTS.settingsTab,
    eventDetail: { tab: "lockout" },
    dispatchDelayMs: 120,
    steps: [
      "Open Settings.",
      "Choose Lockout.",
      "Review state, timers, presets, and website controls.",
    ],
  },
} as const satisfies Record<string, WeeklyWalkthrough>;
